import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Save, ExternalLink,
  Eye, EyeOff, Loader2, Pencil, X, Handshake, ImageIcon, Upload
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import NavigationMenu from '../components/NavigationMenu';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const LogoUploader = ({ value, onChange, token }) => {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/gif', 'image/svg+xml', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast({ title: 'Format non supporté', description: 'PNG, JPG, GIF, SVG ou WebP uniquement', variant: 'destructive' });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: 'Fichier trop volumineux', description: 'Maximum 2 Mo', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/partners/upload-logo`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      onChange(`${BASE}${res.data.logo_url}`);
      toast({ title: 'Logo uploadé' });
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || "Échec de l'upload", variant: 'destructive' });
    }
    setUploading(false);
  }, [token, onChange, toast]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  return (
    <div>
      <label className="block text-sm text-blue-300/70 mb-1.5">Logo</label>
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`relative border-2 border-dashed rounded-xl p-4 text-center transition-all cursor-pointer ${
          dragging ? 'border-teal-400 bg-teal-500/10' : 'border-blue-500/20 hover:border-blue-500/40 bg-slate-900/30'
        }`}
        onClick={() => document.getElementById('logo-file-input')?.click()}
        data-testid="logo-drop-zone"
      >
        <input
          id="logo-file-input"
          type="file"
          accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
          className="hidden"
          onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]); e.target.value = ''; }}
          data-testid="logo-file-input"
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-2 py-2">
            <Loader2 className="w-6 h-6 text-teal-400 animate-spin" />
            <span className="text-sm text-blue-300/60">Upload en cours...</span>
          </div>
        ) : value ? (
          <div className="flex items-center gap-4">
            <img src={value} alt="Logo" className="w-16 h-16 object-contain rounded-lg bg-white/5 border border-blue-500/10 p-1" onError={e => { e.target.style.display = 'none'; }} />
            <div className="flex-1 text-left">
              <p className="text-sm text-white truncate">{value.split('/').pop()}</p>
              <p className="text-xs text-blue-300/40">Cliquez ou glissez pour remplacer</p>
            </div>
            <button
              onClick={e => { e.stopPropagation(); onChange(''); }}
              className="p-1.5 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded-lg"
              data-testid="remove-logo-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 py-2">
            <Upload className="w-6 h-6 text-blue-300/30" />
            <span className="text-sm text-blue-300/50">Glissez un logo ici ou cliquez pour parcourir</span>
            <span className="text-xs text-blue-300/30">PNG, JPG, GIF, SVG, WebP (max 2 Mo)</span>
          </div>
        )}
      </div>
      <div className="mt-2">
        <input
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="...ou collez une URL de logo"
          className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-2 text-white placeholder-blue-300/20 focus:outline-none focus:border-teal-400 text-sm"
          data-testid="partner-logo-input"
        />
      </div>
    </div>
  );
};

const AdminPartners = () => {
  const { token, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [partners, setPartners] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', website_url: '', logo_url: '', is_active: true });

  useEffect(() => {
    if (!loading && !isAdmin) { navigate('/'); return; }
    if (isAdmin && token) loadPartners();
  }, [isAdmin, loading, token, navigate]);

  const loadPartners = async () => {
    setLoadingData(true);
    try {
      const res = await axios.get(`${API}/admin/partners`, { headers: { Authorization: `Bearer ${token}` } });
      setPartners(res.data.partners || []);
    } catch { toast({ title: 'Erreur', description: 'Impossible de charger les partenaires', variant: 'destructive' }); }
    setLoadingData(false);
  };

  const resetForm = () => {
    setForm({ name: '', description: '', website_url: '', logo_url: '', is_active: true });
    setEditingPartner(null);
    setShowForm(false);
  };

  const openEdit = (p) => {
    setForm({ name: p.name, description: p.description || '', website_url: p.website_url || '', logo_url: p.logo_url || '', is_active: p.is_active });
    setEditingPartner(p.partner_id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast({ title: 'Erreur', description: 'Le nom est requis', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      if (editingPartner) {
        await axios.put(`${API}/admin/partners/${editingPartner}`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast({ title: 'Partenaire modifié' });
      } else {
        await axios.post(`${API}/admin/partners`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast({ title: 'Partenaire ajouté' });
      }
      resetForm();
      loadPartners();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Impossible de sauvegarder', variant: 'destructive' });
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce partenaire ?')) return;
    try {
      await axios.delete(`${API}/admin/partners/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Partenaire supprimé' });
      loadPartners();
    } catch { toast({ title: 'Erreur', variant: 'destructive' }); }
  };

  const toggleActive = async (p) => {
    try {
      await axios.put(`${API}/admin/partners/${p.partner_id}`, { is_active: !p.is_active }, { headers: { Authorization: `Bearer ${token}` } });
      loadPartners();
    } catch { toast({ title: 'Erreur', variant: 'destructive' }); }
  };

  if (loading || loadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <Toaster />
      <NavigationMenu />
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard?tab=settings" className="text-blue-400 hover:text-blue-300 transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2" data-testid="admin-partners-title">
                  <Handshake className="w-6 h-6 text-teal-400" />
                  Gestion des partenaires
                </h1>
                <p className="text-blue-300/70 text-sm">Ajoutez et gérez vos partenaires affichés sur le site</p>
              </div>
            </div>
            <Button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="add-partner-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Ajouter
            </Button>
          </div>

          {/* Form */}
          {showForm && (
            <div className="bg-slate-800/70 border border-teal-500/30 rounded-xl p-6 mb-6" data-testid="partner-form">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">
                  {editingPartner ? 'Modifier le partenaire' : 'Nouveau partenaire'}
                </h2>
                <button onClick={resetForm} className="text-blue-300/60 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="grid gap-4">
                <div>
                  <label className="block text-sm text-blue-300/70 mb-1.5">Nom *</label>
                  <input
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="Nom du partenaire"
                    className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-teal-400"
                    data-testid="partner-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-blue-300/70 mb-1.5">Description</label>
                  <textarea
                    value={form.description}
                    onChange={e => setForm({ ...form, description: e.target.value })}
                    placeholder="Courte description du partenaire"
                    rows={2}
                    className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-teal-400 resize-none"
                    data-testid="partner-description-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-blue-300/70 mb-1.5">Site web</label>
                  <input
                    value={form.website_url}
                    onChange={e => setForm({ ...form, website_url: e.target.value })}
                    placeholder="https://example.com"
                    className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-teal-400"
                    data-testid="partner-website-input"
                  />
                </div>
                <LogoUploader
                  value={form.logo_url}
                  onChange={url => setForm({ ...form, logo_url: url })}
                  token={token}
                />
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={e => setForm({ ...form, is_active: e.target.checked })}
                      className="w-4 h-4 rounded border-blue-500/30 bg-slate-800 text-teal-500 focus:ring-teal-500"
                    />
                    <span className="text-sm text-blue-300/70">Actif (visible sur le site)</span>
                  </label>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={resetForm} className="border-blue-500/30 text-blue-300">Annuler</Button>
                  <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-partner-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    {editingPartner ? 'Modifier' : 'Ajouter'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Partners List */}
          {partners.length === 0 ? (
            <div className="bg-slate-800/30 border border-blue-500/10 rounded-xl p-12 text-center" data-testid="no-partners">
              <Handshake className="w-12 h-12 text-blue-300/30 mx-auto mb-4" />
              <p className="text-blue-300/50 text-lg">Aucun partenaire configuré</p>
              <p className="text-blue-300/30 text-sm mt-1">Cliquez sur "Ajouter" pour créer votre premier partenaire</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="partners-list">
              {partners.map(p => (
                <div
                  key={p.partner_id}
                  className={`bg-slate-800/50 border rounded-xl p-4 flex items-center gap-4 transition-all ${p.is_active ? 'border-teal-500/20 hover:border-teal-500/40' : 'border-slate-700/30 opacity-60'}`}
                  data-testid={`partner-item-${p.partner_id}`}
                >
                  <div className="w-14 h-14 rounded-lg bg-slate-700/50 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {p.logo_url ? (
                      <img src={p.logo_url} alt={p.name} className="w-full h-full object-contain p-1" onError={e => { e.target.style.display = 'none'; }} />
                    ) : (
                      <ImageIcon className="w-6 h-6 text-blue-300/30" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium truncate">{p.name}</h3>
                    {p.description && <p className="text-blue-300/50 text-sm truncate">{p.description}</p>}
                    {p.website_url && (
                      <a href={p.website_url} target="_blank" rel="noopener noreferrer" className="text-teal-400/70 text-xs hover:text-teal-300 inline-flex items-center gap-1 mt-0.5">
                        <ExternalLink className="w-3 h-3" /> {p.website_url}
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => toggleActive(p)}
                      className={`p-2 rounded-lg transition-colors ${p.is_active ? 'text-teal-400 hover:bg-teal-500/10' : 'text-blue-300/40 hover:bg-blue-500/10'}`}
                      title={p.is_active ? 'Désactiver' : 'Activer'}
                      data-testid={`toggle-partner-${p.partner_id}`}
                    >
                      {p.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => openEdit(p)}
                      className="p-2 text-blue-300/60 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition-colors"
                      title="Modifier"
                      data-testid={`edit-partner-${p.partner_id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(p.partner_id)}
                      className="p-2 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      title="Supprimer"
                      data-testid={`delete-partner-${p.partner_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminPartners;
